import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography
} from "@mui/material";
import Grid from '@mui/material/Grid';
import React, {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import moment from "moment-timezone";
import {useMutation, useLazyQuery} from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";

import {
    SWAP_SESSION,
    FETCH_SWAPABILITY,
    LOOKUP_SESSION_WITH_CHILDREN_DETAILS,
    QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME
} from "../queries/GraphQLQueries";
import CustomAlert from "./CustomAlert";
import {COMPACT_TIME_FORMAT} from "../config/DateTimeConstants";
import SwapSessionFormControl from "./SwapSessionFormControl";
import { SHIFT_PLAN_ERROR_MESSAGE, ZOOM_MEETING_SWAP_STATUS } from "../domain/ShiftConstants";
import { useDialogNavigation } from "../hooks/useDialogNavigation";

export default function SwapSessionDialog() {

    const navigateBack = useDialogNavigation();
    const {state} = useLocation();

    const [dialogOpen, setDialogOpen] = useState(true);

    const {sourceSessionToSwap} = state || {};

    const [canSwap, setCanSwap] = useState(false);
    const [allSwappableTargetSessions, setAllSwappableTargetSessions] = useState([]);
    const [selectedTargetSession, setSelectedTargetSession] = useState();
    const [alertMessage, setAlertMessage] = useState(null);

    const [fetchSwapOptions, {loading: fetchingLatestSwapData, data}] = useLazyQuery(FETCH_SWAPABILITY, {fetchPolicy: 'network-only'});
    const [swapSession] = useMutation(SWAP_SESSION, {
        refetchQueries: [
          {query: LOOKUP_SESSION_WITH_CHILDREN_DETAILS}, 'GetSessionInfoWithChildrenData',
          {query: QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME}, 'GetTeachingUsersAndTheirAvailability',
        ]
    });

    useEffect(() => {
        setCanSwap(!!selectedTargetSession && (sourceSessionToSwap.seatsCount > 0 || selectedTargetSession.seatsCount > 0));
    }, [selectedTargetSession]);

    useEffect(() => {
        fetchSwapOptions({variables: {sessionId: sourceSessionToSwap.id}})
    }, [sourceSessionToSwap]);

    useEffect(() => {
        if (!data || !data.swapability) {
            return;
        }

        const swappableSessions = data.swapability.map(performerSessionWithSwapRating => {
            return {
                targetShiftId: performerSessionWithSwapRating.targetShift.id,
                targetPerformerId: performerSessionWithSwapRating.targetShift.owner.id,
                targetPerformerName: performerSessionWithSwapRating.targetShift.owner.displayName,
                ruleReport: performerSessionWithSwapRating.ruleReport,
                swapRating: performerSessionWithSwapRating.swapRating,
                targetSessionType: performerSessionWithSwapRating.targetSessions[0]?.sessionType,
                targetSessionServiceType: performerSessionWithSwapRating.targetSessions[0]?.serviceType?.keycode
            };
        });

        setAllSwappableTargetSessions(swappableSessions);
    }, [data]);

    const handleCloseCustomAlert = () => {
        navigateBack();
        setAlertMessage(null);
    };

    const handleCancel = () => {
        setDialogOpen(false);
        navigateBack();
    }

    const handleSwapButtonClick = () => {
        const alertMessage = `Swapping ${sourceSessionToSwap.displayName} 's ${moment(sourceSessionToSwap.from).format(COMPACT_TIME_FORMAT)} 
                            Session to ${selectedTargetSession.targetPerformerName}...`;
        setAlertMessage({ children: alertMessage, severity: 'success' });

        swapSession({
            variables: {
                sessionId: sourceSessionToSwap.id,
                targetShiftId: selectedTargetSession.targetShiftId,
            }
        }).then(response => {
            setDialogOpen(false);
            
            const status = response?.data?.swapSessionAndMeeting?.zoomMeetingSwapStatus;
            
            if (status === ZOOM_MEETING_SWAP_STATUS.SUCCESS) {
                setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SUCCESS_SWAP_STATUS, severity: 'success' });
            } else {
                setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.FAILURE_SWAP_STATUS, severity: 'error' });
            }            
        }).catch(error => {
            setAlertMessage({ children: error?.message, severity: 'error' });
            setCanSwap(false);
            console.log(error);
        });
    }

    const handleSelectedTargetSession = (event) => setSelectedTargetSession(event.target.value);

    const buildSwapSessionDetails = (session) => allSwappableTargetSessions.find(swap => swap.targetPerformerId === session.targetPerformerId);

    const isCriticalSwapRating = (session) => {
        const swapDetails = buildSwapSessionDetails(session);
        return swapDetails?.swapRating === "CRITICAL";
    };

    return <>
        <Dialog open={dialogOpen} maxWidth={"xs"} onClose={handleCancel} disableRestoreFocus>
            <DialogTitle>{`Swap ${sourceSessionToSwap.displayName}'s ${moment(sourceSessionToSwap.from).format(COMPACT_TIME_FORMAT)} ${sourceSessionToSwap.session.serviceType.keycode} Session`}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Grid container>
                        <Grid size={12}>
                            <Typography variant="body1" sx={{fontWeight: 'bold'}}>Service Type</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="body1">{sourceSessionToSwap.session.serviceType.description}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid size={12}>
                            <Typography variant="body1" sx={{fontWeight: 'bold'}}>Content</Typography>
                        </Grid>
                        <Grid size={12}>
                            {sourceSessionToSwap.session.contents.map(content => (
                                <Typography key={content.id} variant="body1">{content.name}</Typography>
                            ))}
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Typography variant="body1" sx={{fontWeight: 'bold'}}>Swap with</Typography>
                        </Grid>
                        <Grid size={12}>
                            {
                                fetchingLatestSwapData
                                  ?
                                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={50}>
                                      <CircularProgress size={20}/>
                                  </Box>
                                  :
                                  <SwapSessionFormControl
                                    allSwappableTargetSessions={allSwappableTargetSessions}
                                    selectedTargetSession={selectedTargetSession}
                                    handleSelectedTargetSession={handleSelectedTargetSession}
                                    sourceSessionToSwap={sourceSessionToSwap} />
                            }
                        </Grid>
                    </Grid>
                    {
                      selectedTargetSession
                      &&
                      isCriticalSwapRating(selectedTargetSession)
                      &&
                      <Typography color="error" sx={{fontWeight: 'bold'}}>
                          Warning
                          <Typography sx={{fontWeight: 'bold'}}>This swap is not recommended</Typography>
                      </Typography>
                    }
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" sx={{textTransform: 'none'}} size="small" onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" sx={{textTransform: 'none'}} size="small" onClick={handleSwapButtonClick} disabled={!canSwap}>Swap Session</Button>
            </DialogActions>
        </Dialog>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </>;
}